/**
 * Activity and per-stint statistics, computed from the (materialised) domain model. Distances,
 * times, strokes and paces are all derived from the lengths so they stay correct after any edit;
 * heart-rate comes straight from Garmin's own lap/session fields (HR is not length-derived, so the
 * watch's values remain authoritative). Pure — no DOM, no state.
 */
import { FID, GLOBAL, getNumber, LAP, SES } from '../domain/fields'
import type { Activity, Stint } from '../domain/model'
import { commonActiveStroke } from '../domain/recompute'
import { deviceName } from './devices'
import { paceSecondsPer100, type DisplayUnit } from './format'
import type { DataRecord } from '../codec/types'

/** A set's stroke: a single swim_stroke value, or 'mixed' when its active lengths disagree. */
export type StintStroke = number | 'mixed'

export interface StintStats {
  readonly index: number
  readonly isRest: boolean
  /** Active distance in metres. */
  readonly distanceM: number
  /** Σ active length durations (ms). */
  readonly movingMs: number
  /** For a rest stint, its total idle duration (ms); 0 otherwise. */
  readonly restMs: number
  readonly activeLengths: number
  /** Moving pace, seconds per 100 display-units; Infinity for a rest stint. */
  readonly paceSecondsPer100: number
  readonly avgHr: number | null
  readonly maxHr: number | null
  /** Average stroke rate, strokes per minute; null when there is no moving time. */
  readonly avgStrokeRate: number | null
  /** The set's stroke (null for a rest stint). */
  readonly stroke: StintStroke | null
}

export interface ActivityStats {
  readonly unit: DisplayUnit
  readonly poolLengthM: number
  readonly distanceM: number
  readonly activeLengths: number
  /** Σ active durations (ms). */
  readonly movingMs: number
  /** Wall-clock elapsed from the session (incl. pauses); never recomputed. */
  readonly elapsedMs: number
  /** Σ idle durations (ms). */
  readonly restMs: number
  readonly movingPaceSecondsPer100: number
  readonly elapsedPaceSecondsPer100: number
  readonly avgHr: number | null
  readonly maxHr: number | null
  readonly avgStrokeRate: number | null
  readonly stints: readonly StintStats[]
  /** Individual rest durations (ms), one per rest stint, in order. */
  readonly rests: readonly number[]
  /** Session start_time as a raw FIT timestamp (seconds since 1989-12-31), for the headline date. */
  readonly startTimeFitEpoch: number | null
  /** The recording device, e.g. "Garmin Forerunner 265", or null if unknown. */
  readonly device: string | null
}

/** uint8 HR: 255 (invalid) and non-positive values read as "no data". */
function hr(value: number | undefined): number | null {
  return value === undefined || value >= 255 || value <= 0 ? null : value
}

function strokeRate(strokes: number, movingMs: number): number | null {
  if (movingMs <= 0) return null
  return (strokes / (movingMs / 1000)) * 60
}

/** The file's display unit: yards iff pool_length_unit is statute, else metres. */
export function displayUnit(activity: Activity): DisplayUnit {
  return getNumber(activity.session, SES.poolLengthUnit) === 1 ? 'yd' : 'm'
}

function stintStats(stint: Stint, index: number, poolM: number, unit: DisplayUnit): StintStats {
  let movingMs = 0
  let restMs = 0
  let strokes = 0
  let activeLengths = 0
  for (const l of stint.lengths) {
    if (l.active) {
      movingMs += l.durationMs
      strokes += l.strokes
      activeLengths += 1
    } else {
      restMs += l.durationMs
    }
  }
  const distanceM = activeLengths * poolM
  return {
    index,
    isRest: stint.isRest,
    distanceM,
    movingMs,
    restMs,
    activeLengths,
    paceSecondsPer100: paceSecondsPer100(distanceM, movingMs, unit),
    avgHr: hr(getNumber(stint.lapRecord, LAP.avgHeartRate)),
    maxHr: hr(getNumber(stint.lapRecord, LAP.maxHeartRate)),
    avgStrokeRate: strokeRate(strokes, movingMs),
    stroke: stintStroke(stint),
  }
}

/** A set's stroke: its common active stroke, 'mixed' if they disagree, or null for a rest. */
function stintStroke(stint: Stint): StintStroke | null {
  if (stint.isRest) return null
  const common = commonActiveStroke(stint.lengths)
  return common === undefined ? 'mixed' : common
}

export function computeStats(activity: Activity): ActivityStats {
  const unit = displayUnit(activity)
  const poolM = activity.poolLengthCm / 100

  const stints = activity.stints.map((s, i) => stintStats(s, i, poolM, unit))

  let movingMs = 0
  let restMs = 0
  let strokes = 0
  let activeLengths = 0
  for (const s of stints) {
    movingMs += s.movingMs
    restMs += s.restMs
    activeLengths += s.activeLengths
    // strokes accumulated below from the model to avoid re-reading
  }
  for (const l of activity.lengths) if (l.active) strokes += l.strokes

  const distanceM = activeLengths * poolM
  const elapsedMs = getNumber(activity.session, SES.totalElapsed) ?? movingMs + restMs

  return {
    unit,
    poolLengthM: poolM,
    distanceM,
    activeLengths,
    movingMs,
    elapsedMs,
    restMs,
    movingPaceSecondsPer100: paceSecondsPer100(distanceM, movingMs, unit),
    elapsedPaceSecondsPer100: paceSecondsPer100(distanceM, elapsedMs, unit),
    avgHr: hr(getNumber(activity.session, SES.avgHeartRate)),
    maxHr: hr(getNumber(activity.session, SES.maxHeartRate)),
    avgStrokeRate: strokeRate(strokes, movingMs),
    stints,
    rests: stints.filter((s) => s.isRest).map((s) => s.restMs),
    startTimeFitEpoch: getNumber(activity.session, SES.startTime) ?? null,
    device: resolveDevice(activity),
  }
}

function resolveDevice(activity: Activity): string | null {
  const fileId = activity.file.records.find(
    (r): r is DataRecord => r.kind === 'data' && r.def.globalMessageNumber === GLOBAL.fileId,
  )
  if (!fileId) return null
  return deviceName(getNumber(fileId, FID.manufacturer), getNumber(fileId, FID.product))
}
